import { getAppLocale, toAcceptLanguage } from '@repo/common-utils/utils/appLocale'
import { UIN } from '@repo/common-utils/constants/user'

/**
 * API Key 用户端接口层。
 * 合一接口 POST /api/v1/api-key：以 X-uin 鉴权，uin 与 apiKey 一一对应（幂等）。
 * 走 `/api` 相对路径，由 Vite dev proxy / 前置网关转发，CORS 由网关处理。
 */

const API_BASE = (
  import.meta.env.DEV ? '' : ((import.meta.env.VITE_SKILLHUB_API_BASE as string) ?? '')
).replace(/\/$/, '')

function apiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${p}`
}

export interface ApiKeyResponse {
  uin: string
  api_key: string
  created: boolean
  created_at: string
}

/** 后端错误，携带 http 状态与 error.code，供文案映射。 */
export class ApiKeyError extends Error {
  httpStatus: number
  code: string
  requestId?: string
  constructor(httpStatus: number, code: string, message: string, requestId?: string) {
    super(message)
    this.name = 'ApiKeyError'
    this.httpStatus = httpStatus
    this.code = code
    this.requestId = requestId
  }
}

function getUin(): string {
  try {
    return localStorage.getItem(UIN) || ''
  } catch {
    return ''
  }
}

/** 打码展示：保留 fsk_ 前缀 + 前 4 位，其余以 * 补全至 47 位；不可反推明文。 */
export function maskApiKey(key: string): string {
  if (!key) return ''
  const TOTAL_LEN = 26
  const prefix = key.startsWith('fsk_') ? 'fsk_' : ''
  const head = key.slice(prefix.length, prefix.length + 4)
  const asterisks = '*'.repeat(TOTAL_LEN - prefix.length - head.length)
  return `${prefix}${head}${asterisks}`
}

/**
 * 生成/查询 apiKey（合一、幂等）。
 * 成功返回 ApiKeyResponse；非 2xx 抛 ApiKeyError。
 */
export async function fetchApiKey(): Promise<ApiKeyResponse> {
  const res = await fetch(apiUrl('/api/v1/api-key'), {
    method: 'POST',
    headers: {
      'Accept-Language': toAcceptLanguage(getAppLocale()),
      Accept: 'application/json',
      'X-uin': getUin(),
    },
  })

  const requestId = res.headers.get('x-request-id') ?? undefined
  if (!res.ok) {
    let code = 'internal'
    let message = res.statusText
    try {
      const body = await res.json()
      code = body?.error?.code ?? code
      message = body?.error?.message ?? message
    } catch {
      /* 非 JSON 错误体，忽略 */
    }
    throw new ApiKeyError(res.status, code, message, requestId)
  }
  return res.json() as Promise<ApiKeyResponse>
}

/** 错误 → 前端文案 i18n key（对齐后端联调文档）。 */
export function apiKeyErrorI18nKey(err: unknown): string {
  if (err instanceof ApiKeyError) {
    if (err.httpStatus === 401 && /missing/i.test(err.message)) return 'apiKey.err.needLogin'
    if (err.httpStatus === 400) return 'apiKey.err.badUin'
    if (err.httpStatus === 401) return 'apiKey.err.verifyFail'
    if (err.httpStatus === 403) return 'apiKey.err.revoked'
  }
  return 'apiKey.err.internal'
}
